import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Rack, RackLevel, RackPosition, ProductLocation,
  fetchEstructuraZona, fetchUbicacionesZona, crearRack, actualizarRack,
  actualizarCodigoNivel, actualizarCodigoPosicion, borrarRack, productosDelRack,
  asignarProductos, quitarUbicacion,
} from '../../lib/ubicaciones';
import { advertenciasMismoRack } from '../../lib/ubicaciones-reglas';
import {
  codigoCompleto, sugerirSiguienteRack, validarRack, vistaPreviaRack,
  MAX_NIVELES, MAX_POSICIONES, NIVELES_BAJOS_POR_DEFECTO,
} from '../../lib/ubicaciones-codigos';

// Racks, niveles y posiciones de una zona (oculto tras ubicacionesHabilitadas).
// Permisos como en warehouse_zones: ver todos; crear/editar admin y manager;
// borrar solo admin (la base lo exige igual por RLS).

export interface ZonaParaRacks { id: string; name: string; code: string | null; product_ids: string[] }
export interface ProductoParaRacks { id: string; name: string; sga_classes: string[] }

interface Props {
  zone: ZonaParaRacks;
  products: ProductoParaRacks[];
  puedeEditar: boolean;
  puedeBorrar: boolean;
  onClose: () => void;
  onZonaCambiada: () => void;
}

type Seleccion = { rackId: string; levelId: string | null; positionId: string | null };

export default function ZonaRacksModal({ zone, products, puedeEditar, puedeBorrar, onClose, onZonaCambiada }: Props) {
  const [racks, setRacks] = useState<Rack[]>([]);
  const [ubicaciones, setUbicaciones] = useState<ProductLocation[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [form, setForm] = useState({ code: '', levels: 5, positions: 8, low: NIVELES_BAJOS_POR_DEFECTO });
  const [formError, setFormError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const [editando, setEditando] = useState<Rack | null>(null);
  const [editForm, setEditForm] = useState({ code: '', low: NIVELES_BAJOS_POR_DEFECTO });

  const [seleccion, setSeleccion] = useState<Seleccion | null>(null);
  const [codigoSel, setCodigoSel] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [marcados, setMarcados] = useState<string[]>([]);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const [r, u] = await Promise.all([fetchEstructuraZona(zone.id), fetchUbicacionesZona(zone.id)]);
      setRacks(r); setUbicaciones(u);
    } catch (e) {
      setError('No se pudieron cargar los racks: ' + (e as Error).message);
    }
    setCargando(false);
  }, [zone.id]);

  useEffect(() => { cargar(); }, [cargar]);

  const nombrePorId = useMemo(() => new Map(products.map(p => [p.id, p.name])), [products]);
  const advertencias = useMemo(
    () => new Map(advertenciasMismoRack(racks.map(r => r.id), ubicaciones, products).map(a => [a.rackId, a.pares])),
    [racks, ubicaciones, products],
  );
  const flash = (msg: string) => { setAviso(msg); setTimeout(() => setAviso(''), 3000); };

  // ── Crear rack (generación masiva) ──────────────────────────────────────
  const abrirNuevo = () => {
    setForm({ code: sugerirSiguienteRack(racks.map(r => r.code)), levels: 5, positions: 8, low: NIVELES_BAJOS_POR_DEFECTO });
    setFormError(''); setNuevoAbierto(true);
  };
  const vista = vistaPreviaRack(zone.code, form.code, form.levels || 1, form.positions || 0);
  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validarRack(form.code, form.levels, form.positions, form.low);
    if (!v.ok) { setFormError(v.error ?? ''); return; }
    setGuardando(true); setFormError('');
    try {
      await crearRack(zone.id, form.code, form.levels, form.positions, form.low);
      setNuevoAbierto(false); flash(`Rack ${form.code.trim()} creado.`); await cargar();
    } catch (err) { setFormError((err as Error).message); }
    setGuardando(false);
  };

  // ── Editar rack ─────────────────────────────────────────────────────────
  const abrirEditar = (r: Rack) => { setEditando(r); setEditForm({ code: r.code, low: r.low_levels }); setFormError(''); };
  const guardarEdicion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editando) return;
    const v = validarRack(editForm.code, Math.max(editando.levels.length, 1), 0, editForm.low);
    if (!v.ok) { setFormError(v.error ?? ''); return; }
    setGuardando(true); setFormError('');
    try {
      await actualizarRack(editando.id, { code: editForm.code.trim(), low_levels: editForm.low });
      setEditando(null); flash('Rack actualizado.'); await cargar();
    } catch (err) { setFormError((err as Error).message); }
    setGuardando(false);
  };

  // ── Borrar rack ─────────────────────────────────────────────────────────
  const eliminar = async (r: Rack) => {
    const n = productosDelRack(ubicaciones, r.id).length;
    const detalle = n > 0
      ? `Tiene ${n} producto${n !== 1 ? 's' : ''} asignado${n !== 1 ? 's' : ''}: se quitarán sus ubicaciones en este rack, pero seguirán en la zona.`
      : 'No tiene productos asignados.';
    if (!window.confirm(`¿Eliminar el rack ${r.code}? ${detalle}`)) return;
    try { await borrarRack(r.id); flash(`Rack ${r.code} eliminado.`); setSeleccion(null); await cargar(); }
    catch (err) { setError((err as Error).message); }
  };

  // ── Selección de celda (nivel o posición) ───────────────────────────────
  const rackSel = seleccion ? racks.find(r => r.id === seleccion.rackId) ?? null : null;
  const nivelSel: RackLevel | null = rackSel && seleccion?.levelId ? rackSel.levels.find(l => l.id === seleccion.levelId) ?? null : null;
  const posSel: RackPosition | null = nivelSel && seleccion?.positionId ? nivelSel.positions.find(p => p.id === seleccion.positionId) ?? null : null;
  const seleccionar = (s: Seleccion, codigoActual: string) => { setSeleccion(s); setCodigoSel(codigoActual); setBusqueda(''); setMarcados([]); };
  const guardarCodigoSel = async () => {
    if (!seleccion || !codigoSel.trim()) return;
    try {
      if (posSel) await actualizarCodigoPosicion(posSel.id, codigoSel);
      else if (nivelSel) await actualizarCodigoNivel(nivelSel.id, codigoSel);
      flash('Código actualizado.'); await cargar();
    } catch (err) { setError((err as Error).message); }
  };

  const ubicacionesEn = (rackId: string, levelId: string | null, positionId: string | null) =>
    ubicaciones.filter(u => u.rack_id === rackId && u.level_id === levelId && u.position_id === positionId);

  // ── Asignar / quitar en la ubicación seleccionada ───────────────────────
  const enSeleccion = seleccion ? ubicacionesEn(seleccion.rackId, seleccion.levelId, seleccion.positionId) : [];
  const yaAqui = new Set(enSeleccion.map(u => u.product_id));
  const candidatos = products
    .filter(p => !yaAqui.has(p.id) && p.name.toLowerCase().includes(busqueda.trim().toLowerCase()))
    .sort((a, b) => Number(zone.product_ids.includes(b.id)) - Number(zone.product_ids.includes(a.id)) || a.name.localeCompare(b.name, 'es'));
  const asignar = async () => {
    if (!seleccion || marcados.length === 0) return;
    const fuera = marcados.filter(id => !zone.product_ids.includes(id)).length;
    try {
      await asignarProductos({ zoneId: zone.id, ...seleccion }, marcados);
      flash(`${marcados.length} producto${marcados.length !== 1 ? 's' : ''} asignado${marcados.length !== 1 ? 's' : ''}.`);
      setMarcados([]); await cargar();
      if (fuera > 0) onZonaCambiada();   // el trigger los agregó a la zona
    } catch (err) { setError((err as Error).message); }
  };
  const quitar = async (locationId: string, nombre: string) => {
    if (!window.confirm(`¿Quitar ${nombre} de esta ubicación? Seguirá en la zona.`)) return;
    try { await quitarUbicacion(locationId); flash('Ubicación quitada.'); await cargar(); }
    catch (err) { setError((err as Error).message); }
  };

  const iniciales = (ids: string[]) => ids.map(id => (nombrePorId.get(id) ?? '?').slice(0, 2).toUpperCase()).join(' ');

  return (
    <div className="wh-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wh-modal wh-modal-racks">
        <div className="wh-modal-header">
          <h4>Racks y ubicaciones · {zone.name}{zone.code ? ` (${zone.code})` : ''}</h4>
          <button className="wh-modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        <div className="wh-rk-body">
          {!zone.code && (
            <p className="wh-rk-note">Esta zona no tiene código corto. Edítala y agrega uno (por ejemplo Z1) para que los códigos queden como Z1-R03-N2-P05.</p>
          )}
          <div className="wh-rk-toolbar">
            <span className="wh-rk-legend">N1 es el nivel de abajo, junto al piso. <span className="wh-rk-low-dot" /> nivel bajo (a la altura del hombro o por debajo).</span>
            {puedeEditar && <button className="wh-btn-add" onClick={abrirNuevo}>+ Rack</button>}
          </div>

          {aviso && <div className="wh-rk-flash">{aviso}</div>}
          {error && <p className="wh-error">{error}</p>}

          {cargando ? (
            <div className="wh-loading"><span className="wh-spinner" /><p>Cargando racks…</p></div>
          ) : racks.length === 0 ? (
            <p className="wh-zone-no-prods" style={{ padding: '16px 0' }}>Esta zona no tiene racks. Los productos están en la zona sin rack.</p>
          ) : racks.map(rack => {
            const nPos = Math.max(0, ...rack.levels.map(l => l.positions.length));
            const enRack = productosDelRack(ubicaciones, rack.id);
            return (
              <div key={rack.id} className="wh-rk-rack">
                <div className="wh-rk-rack-head">
                  <div>
                    <strong>{codigoCompleto({ zona: zone.code, rack: rack.code })}</strong>
                    <span className="wh-rk-meta">
                      {rack.levels.length} nivel{rack.levels.length !== 1 ? 'es' : ''}
                      {nPos > 0 ? ` × ${nPos} posiciones` : ' sin posiciones'} · niveles bajos: {rack.low_levels} · {enRack.length} producto{enRack.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="wh-actions">
                    <button className="wh-btn-racks" title="Productos en el rack sin nivel definido" onClick={() => seleccionar({ rackId: rack.id, levelId: null, positionId: null }, rack.code)}>
                      Sin nivel{ubicacionesEn(rack.id, null, null).length > 0 ? ` (${ubicacionesEn(rack.id, null, null).length})` : ''}
                    </button>
                    {puedeEditar && <button className="wh-btn-icon wh-btn-edit" title="Editar rack" onClick={() => abrirEditar(rack)}>✎</button>}
                    {puedeBorrar && <button className="wh-btn-icon wh-btn-delete" title="Eliminar rack" onClick={() => eliminar(rack)}>🗑</button>}
                  </div>
                </div>

                {advertencias.get(rack.id) && (
                  <div className="wh-alert incompatible">
                    <span aria-hidden="true">⚠</span>
                    <div className="wh-alert-body">
                      <span className="wh-alert-title">Buena práctica: productos incompatibles en el mismo rack</span>
                      <span className="wh-alert-detail">{advertencias.get(rack.id)!.map(p => `${p.a.name} + ${p.b.name}`).join(' · ')}</span>
                    </div>
                  </div>
                )}

                <div className="wh-rk-grid-wrap">
                  <table className="wh-rk-grid">
                    <tbody>
                      {[...rack.levels].reverse().map(level => {
                        const bajo = level.level_number <= rack.low_levels;
                        const enNivel = ubicacionesEn(rack.id, level.id, null);
                        return (
                          <tr key={level.id}>
                            <th
                              className={`wh-rk-level${bajo ? ' low' : ''}${seleccion?.levelId === level.id && !seleccion?.positionId ? ' sel' : ''}`}
                              onClick={() => seleccionar({ rackId: rack.id, levelId: level.id, positionId: null }, level.code)}
                              title={`Nivel ${level.code}${bajo ? ' (bajo)' : ''}`}
                            >
                              {level.code}{enNivel.length > 0 && <span className="wh-rk-count">{enNivel.length}</span>}
                            </th>
                            {level.positions.map(pos => {
                              const aqui = ubicacionesEn(rack.id, level.id, pos.id).map(u => u.product_id);
                              const sel = seleccion?.positionId === pos.id;
                              return (
                                <td
                                  key={pos.id}
                                  className={`wh-rk-cell${aqui.length ? ' filled' : ''}${sel ? ' sel' : ''}`}
                                  onClick={() => seleccionar({ rackId: rack.id, levelId: level.id, positionId: pos.id }, pos.code)}
                                  title={`${codigoCompleto({ zona: zone.code, rack: rack.code, nivel: level.code, posicion: pos.code })}${aqui.length ? ': ' + aqui.map(id => nombrePorId.get(id) ?? '?').join(', ') : ''}`}
                                >
                                  {aqui.length ? iniciales(aqui) : pos.code}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {seleccion && rackSel && (
            <div className="wh-rk-panel">
              <div className="wh-rk-panel-head">
                <strong>{codigoCompleto({ zona: zone.code, rack: rackSel.code, nivel: nivelSel?.code, posicion: posSel?.code })}</strong>
                <button className="wh-btn-cancel" onClick={() => setSeleccion(null)}>Cerrar</button>
              </div>
              {puedeEditar && (nivelSel || posSel) && (
                <div className="wh-rk-inline">
                  <label>{posSel ? 'Código de la posición' : 'Código del nivel'}</label>
                  <input value={codigoSel} maxLength={20} onChange={e => setCodigoSel(e.target.value)} />
                  <button className="wh-btn-save" onClick={guardarCodigoSel} disabled={!codigoSel.trim()}>Guardar código</button>
                </div>
              )}

              <div className="wh-rk-assigned">
                <span className="wh-rk-subtitle">Productos en esta ubicación ({enSeleccion.length})</span>
                {enSeleccion.length === 0 ? (
                  <p className="wh-rk-note">Ninguno.</p>
                ) : enSeleccion.map(u => (
                  <div key={u.id} className="wh-prod-row">
                    <span className="wh-prod-name">{nombrePorId.get(u.product_id) ?? 'Producto no disponible'}</span>
                    {puedeBorrar && (
                      <button className="wh-btn-cancel" onClick={() => quitar(u.id, nombrePorId.get(u.product_id) ?? 'el producto')}>Quitar de esta ubicación</button>
                    )}
                  </div>
                ))}
              </div>

              {puedeEditar && (
                <div className="wh-rk-assign">
                  <span className="wh-rk-subtitle">Asignar productos aquí</span>
                  <input className="wh-search" placeholder="Buscar producto…" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
                  <div className="wh-prod-list">
                    {candidatos.length === 0 ? (
                      <p className="wh-no-prods">Sin productos para asignar.</p>
                    ) : candidatos.map(p => {
                      const checked = marcados.includes(p.id);
                      return (
                        <label key={p.id} className={`wh-prod-item ${checked ? 'checked' : ''}`}>
                          <input type="checkbox" checked={checked}
                            onChange={() => setMarcados(m => (checked ? m.filter(x => x !== p.id) : [...m, p.id]))} />
                          <span className="wh-prod-item-name">{p.name}</span>
                          {!zone.product_ids.includes(p.id) && <span className="wh-rk-note">(se agregará a la zona)</span>}
                        </label>
                      );
                    })}
                  </div>
                  <button className="wh-btn-save" onClick={asignar} disabled={marcados.length === 0}>
                    Asignar{marcados.length ? ` (${marcados.length})` : ''}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Nuevo rack ── */}
        {nuevoAbierto && (
          <div className="wh-overlay" onClick={(e) => e.target === e.currentTarget && setNuevoAbierto(false)}>
            <div className="wh-modal">
              <div className="wh-modal-header">
                <h4>Nuevo rack · {zone.name}</h4>
                <button className="wh-modal-close" onClick={() => setNuevoAbierto(false)} aria-label="Cerrar">✕</button>
              </div>
              <form className="wh-form" onSubmit={crear}>
                <div className="wh-field">
                  <label>Código del rack <span className="wh-required">*</span></label>
                  <input value={form.code} maxLength={20} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} autoFocus />
                </div>
                <div className="wh-rk-row3">
                  <div className="wh-field">
                    <label>Niveles (1–{MAX_NIVELES})</label>
                    <input type="number" min={1} max={MAX_NIVELES} value={form.levels}
                      onChange={e => setForm(f => ({ ...f, levels: parseInt(e.target.value, 10) || 0 }))} />
                  </div>
                  <div className="wh-field">
                    <label>Posiciones por nivel (0–{MAX_POSICIONES})</label>
                    <input type="number" min={0} max={MAX_POSICIONES} value={form.positions}
                      onChange={e => setForm(f => ({ ...f, positions: parseInt(e.target.value, 10) || 0 }))} />
                  </div>
                  <div className="wh-field">
                    <label>Niveles bajos</label>
                    <input type="number" min={0} max={form.levels || 0} value={form.low}
                      onChange={e => setForm(f => ({ ...f, low: parseInt(e.target.value, 10) || 0 }))} />
                  </div>
                </div>
                <p className="wh-rk-note">
                  Niveles bajos: cuántos niveles, contando desde el piso, están a la altura del hombro o por debajo.
                </p>
                <p className="wh-rk-preview">
                  Vista previa: <strong>{vista.primero}</strong> … <strong>{vista.ultimo}</strong> · {vista.total} {vista.unidad}
                </p>
                {formError && <p className="wh-error">{formError}</p>}
                <div className="wh-modal-footer">
                  <button type="button" className="wh-btn-cancel" onClick={() => setNuevoAbierto(false)}>Cancelar</button>
                  <button type="submit" className="wh-btn-save" disabled={guardando}>{guardando ? 'Creando…' : 'Crear rack'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Editar rack ── */}
        {editando && (
          <div className="wh-overlay" onClick={(e) => e.target === e.currentTarget && setEditando(null)}>
            <div className="wh-modal">
              <div className="wh-modal-header">
                <h4>Editar rack {editando.code}</h4>
                <button className="wh-modal-close" onClick={() => setEditando(null)} aria-label="Cerrar">✕</button>
              </div>
              <form className="wh-form" onSubmit={guardarEdicion}>
                <div className="wh-field">
                  <label>Código del rack <span className="wh-required">*</span></label>
                  <input value={editForm.code} maxLength={20} onChange={e => setEditForm(f => ({ ...f, code: e.target.value }))} autoFocus />
                </div>
                <div className="wh-field">
                  <label>Niveles bajos (0–{editando.levels.length})</label>
                  <input type="number" min={0} max={editando.levels.length} value={editForm.low}
                    onChange={e => setEditForm(f => ({ ...f, low: parseInt(e.target.value, 10) || 0 }))} />
                </div>
                <p className="wh-rk-note">Los códigos de cada nivel y posición se editan haciendo clic en la grilla.</p>
                {formError && <p className="wh-error">{formError}</p>}
                <div className="wh-modal-footer">
                  <button type="button" className="wh-btn-cancel" onClick={() => setEditando(null)}>Cancelar</button>
                  <button type="submit" className="wh-btn-save" disabled={guardando}>Guardar cambios</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
